import React from 'react';
import type { FormInstance } from 'antd';
import { Form } from 'antd';
import AlignLeftOutlined from '@ant-design/icons/AlignLeftOutlined';
import AlignCenterOutlined from '@ant-design/icons/AlignCenterOutlined';
import AlignRightOutlined from '@ant-design/icons/AlignRightOutlined';
import MenuOutlined from '@ant-design/icons/MenuOutlined';
import { RibbonIconButton } from '../bookDefaultStyles/WordStyleRibbon';
import { styleFieldName } from '../../utils/styleFormFieldNames';
import type { ContentAlign } from '../../utils/bookDefaults';

type Props = {
  form: FormInstance;
  namePrefix?: string | string[];
};

/** Justified lines with a centered last line — matches ant-design align icon style. */
const JustifyAlignCenterOutlined = () => (
  <span role="img" aria-hidden className="anticon">
    <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
      <path d="M904 160H120c-4.4 0-8 3.6-8 8v64c0 4.4 3.6 8 8 8h784c4.4 0 8-3.6 8-8v-64c0-4.4-3.6-8-8-8z" />
      <path d="M904 352H120c-4.4 0-8 3.6-8 8v64c0 4.4 3.6 8 8 8h784c4.4 0 8-3.6 8-8v-64c0-4.4-3.6-8-8-8z" />
      <path d="M760 544H264c-4.4 0-8 3.6-8 8v64c0 4.4 3.6 8 8 8h496c4.4 0 8-3.6 8-8v-64c0-4.4-3.6-8-8-8z" />
    </svg>
  </span>
);

const ALIGNMENTS: Array<{ value: ContentAlign; title: string; icon: React.ReactNode }> = [
  { value: 'left', title: 'Align left', icon: <AlignLeftOutlined /> },
  { value: 'center', title: 'Align center', icon: <AlignCenterOutlined /> },
  { value: 'right', title: 'Align right', icon: <AlignRightOutlined /> },
  { value: 'justify', title: 'Justify', icon: <MenuOutlined /> },
  { value: 'justify-center', title: 'Justify align center', icon: <JustifyAlignCenterOutlined /> },
];

type AlignValue = ContentAlign;

const AlignButtonGroup: React.FC<{
  value?: AlignValue;
  onChange?: (value: AlignValue) => void;
}> = ({ value = 'left', onChange }) => (
  <div className="word-ribbon-inline">
    {ALIGNMENTS.map(({ value: alignValue, title, icon }) => (
      <RibbonIconButton
        key={alignValue}
        title={title}
        active={value === alignValue}
        onClick={() => onChange?.(alignValue)}
      >
        {icon}
      </RibbonIconButton>
    ))}
  </div>
);

/** Icon toggles wired to Ant Design Form.Item (single `align` field; saved as textAlign on apply). */
export const AlignToggleButtons: React.FC<Props> = ({ form, namePrefix }) => {
  const alignField = styleFieldName(namePrefix, 'align');
  const indentField = styleFieldName(namePrefix, 'textIndent');
  const value = Form.useWatch(alignField, form) as AlignValue | undefined;

  return (
    <Form.Item name={alignField} initialValue="left" noStyle>
      <AlignButtonGroup
        value={value}
        onChange={(next) => {
          form.setFieldValue(alignField, next);
          // Align changes should reset indentation so the layout stays predictable.
          form.setFieldValue(indentField, 0);
        }}
      />
    </Form.Item>
  );
};
